'use client';

import { Star, Users, Building2 } from 'lucide-react';

interface SocialProofProps {
  g2Rating?: number | null;
  g2ReviewCount?: number | null;
  capterraRating?: number | null;
  capterraReviewCount?: number | null;
  customerCount?: string | null;
  notableCustomers?: string[] | null;
  className?: string;
}

function RatingStars({ rating, maxRating = 5 }: { rating: number; maxRating?: number }) {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = maxRating - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="flex items-center gap-1">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
      ))}
      {hasHalfStar && (
        <div className="relative">
          <Star className="w-5 h-5 text-gray-300" />
          <div className="absolute inset-0 overflow-hidden w-1/2">
            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
          </div>
        </div>
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} className="w-5 h-5 text-gray-300" />
      ))}
    </div>
  );
}

export default function SocialProof({
  g2Rating,
  g2ReviewCount,
  capterraRating,
  capterraReviewCount,
  customerCount,
  notableCustomers,
  className = '',
}: SocialProofProps) {
  // Check if we have any social proof data
  const hasRatings = g2Rating || capterraRating;
  const hasCustomerData = customerCount || (notableCustomers && notableCustomers.length > 0);

  if (!hasRatings && !hasCustomerData) {
    return null;
  }

  // Count how many stat cards we'll display
  const statCount = (g2Rating ? 1 : 0) + (capterraRating ? 1 : 0) + (customerCount ? 1 : 0);

  return (
    <div className={className}>
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 sm:mb-6 text-center">
        Trusted by Thousands
      </h3>

      {/* Stats Grid - responsive columns based on content */}
      <div className={`grid gap-4 sm:gap-6 mb-6 ${
        statCount === 3 ? 'grid-cols-1 sm:grid-cols-3' :
        statCount === 2 ? 'grid-cols-1 sm:grid-cols-2' :
        'grid-cols-1'
      }`}>
        {/* G2 Rating Card */}
        {g2Rating && (
          <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-100 rounded-xl p-5 sm:p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-orange-100 rounded-xl mx-auto mb-3">
              <span className="text-orange-600 font-bold text-lg sm:text-xl">G2</span>
            </div>
            <div className="flex items-center justify-center gap-1 mb-2">
              <RatingStars rating={g2Rating} />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{g2Rating.toFixed(1)}</p>
            {g2ReviewCount && (
              <p className="text-sm sm:text-base text-gray-500 mt-1">{g2ReviewCount.toLocaleString()} reviews</p>
            )}
          </div>
        )}

        {/* Capterra Rating Card */}
        {capterraRating && (
          <div className="bg-gradient-to-br from-teal-50 to-white border border-teal-100 rounded-xl p-5 sm:p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-teal-100 rounded-xl mx-auto mb-3">
              <span className="text-teal-600 font-bold text-xs sm:text-sm">Capterra</span>
            </div>
            <div className="flex items-center justify-center gap-1 mb-2">
              <RatingStars rating={capterraRating} />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{capterraRating.toFixed(1)}</p>
            {capterraReviewCount && (
              <p className="text-sm sm:text-base text-gray-500 mt-1">{capterraReviewCount.toLocaleString()} reviews</p>
            )}
          </div>
        )}

        {/* Customer Count Card */}
        {customerCount && (
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-xl p-5 sm:p-6 text-center">
            <div className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-blue-100 rounded-xl mx-auto mb-3">
              <Users className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{customerCount}</p>
            <p className="text-sm sm:text-base text-gray-500 mt-1">customers worldwide</p>
          </div>
        )}
      </div>

      {/* Notable Customers - full width */}
      {notableCustomers && notableCustomers.length > 0 && (
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 sm:p-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Building2 className="w-5 h-5 text-gray-400" />
            <span className="text-sm sm:text-base text-gray-500 font-medium">Trusted by leading organizations</span>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {notableCustomers.map((customer) => (
              <span
                key={customer}
                className="inline-flex items-center px-4 py-2.5 min-h-[44px] bg-white border border-gray-200 rounded-full text-sm sm:text-base text-gray-700 font-medium shadow-sm"
              >
                {customer}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
